import React from 'react'
import { useDashboard } from '@/hooks/useDashboard'
import { PageHeader } from '@/components/ui/PageHeader'
import { AlertCircle, BarChart3 } from 'lucide-react'

const stageLabels: Record<string, string> = {
  Review: 'Review',
  PhoneScreen: 'Phone Screen',
  Interview: 'Interview',
  Assessment: 'Assessment',
  ReferenceCheck: 'Reference Check',
  Offer: 'Offer',
  Outcome: 'Outcome',
}

const Dashboard: React.FC = () => {
  const { dashboard, isLoading, error, refetch } = useDashboard()

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-white)',
    border: '1px solid var(--color-light-grey)',
    borderRadius: '8px',
    padding: '20px',
  }

  const metricCardStyle: React.CSSProperties = {
    ...cardStyle,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    minHeight: '120px',
  }

  const metricValueStyle: React.CSSProperties = {
    fontSize: '32px',
    fontWeight: 700,
    color: 'var(--color-magenta)',
    fontFamily: 'var(--font-family)',
  }

  const metricLabelStyle: React.CSSProperties = {
    fontSize: '13px',
    color: 'var(--color-dark-grey)',
    fontFamily: 'var(--font-family)',
    marginTop: '4px',
  }

  const tableStyle: React.CSSProperties = {
    width: '100%',
    borderCollapse: 'collapse',
    fontFamily: 'var(--font-family)',
  }

  const thStyle: React.CSSProperties = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--color-dark-grey)',
    textTransform: 'uppercase',
    borderBottom: '1px solid var(--color-light-grey)',
  }

  const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    fontSize: '14px',
    color: 'var(--color-black)',
    borderBottom: '1px solid var(--color-light-grey)',
  }

  if (isLoading) {
    return (
      <div style={{ padding: '0' }}>
        <PageHeader title="TAP Dashboard" />
        <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-dark-grey)' }}>
          Loading dashboard...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: '0' }}>
        <PageHeader
          title="TAP Dashboard"
          actions={
            <button
              onClick={() => refetch()}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-magenta)',
                color: 'var(--color-white)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Retry
            </button>
          }
        />
        <div style={{ padding: '24px' }}>
          <div style={{
            backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px',
            padding: '12px 16px', display: 'flex', gap: '10px', alignItems: 'flex-start',
          }}>
            <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0, marginTop: '2px' }} />
            <div>
              <p style={{ margin: 0, color: '#dc2626', fontWeight: 500 }}>Error loading dashboard</p>
              <p style={{ margin: '4px 0 0 0', color: '#991b1b', fontSize: '13px' }}>{error.message}</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!dashboard) return null

  const byStageEntries = Object.entries(dashboard.byStage || {})
  const byVacancyEntries = Object.entries(dashboard.byVacancyCode || {})
  const avgTimeEntries = Object.entries(dashboard.avgTimeInStage || {})
  const conversionEntries = Object.entries(dashboard.conversionRates || {})
  const sourceEntries = Object.entries(dashboard.sourceBreakdown || {})

  return (
    <div style={{ padding: '0' }}>
      <PageHeader
        title="TAP Dashboard"
        subtitle="Talent Acquisition reporting and metrics"
        actions={
          <button
            onClick={() => refetch()}
            style={{
              padding: '8px 16px',
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-black)',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            Refresh
          </button>
        }
      />

      <div style={{ padding: '24px' }}>
        {/* Summary cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div style={metricCardStyle}>
            <div style={metricValueStyle}>{dashboard.totalApplications}</div>
            <div style={metricLabelStyle}>Total Applications</div>
          </div>
          {dashboard.avgTimeToHire != null && (
            <div style={metricCardStyle}>
              <div style={metricValueStyle}>{Math.round(dashboard.avgTimeToHire)}</div>
              <div style={metricLabelStyle}>Avg Days to Hire</div>
            </div>
          )}
          {byStageEntries.map(([stage, count]) => (
            <div key={stage} style={metricCardStyle}>
              <div style={{ ...metricValueStyle, fontSize: '28px' }}>{count}</div>
              <div style={metricLabelStyle}>{stageLabels[stage] || stage}</div>
            </div>
          ))}
        </div>

        {/* Breakdown tables */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* By Vacancy Code */}
          {byVacancyEntries.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart3 size={18} style={{ color: 'var(--color-magenta)' }} />
                By Vacancy Code
              </h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Vacancy Code</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {byVacancyEntries.map(([code, count]) => (
                    <tr key={code}>
                      <td style={tdStyle}>{code || '(none)'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Average Time in Stage */}
          {avgTimeEntries.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart3 size={18} style={{ color: 'var(--color-magenta)' }} />
                Avg Time in Stage (days)
              </h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Stage</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Days</th>
                  </tr>
                </thead>
                <tbody>
                  {avgTimeEntries.map(([stage, days]) => (
                    <tr key={stage}>
                      <td style={tdStyle}>{stageLabels[stage] || stage}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{Math.round(days)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Conversion Rates */}
          {conversionEntries.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart3 size={18} style={{ color: 'var(--color-magenta)' }} />
                Conversion Rates
              </h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Transition</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionEntries.map(([transition, rate]) => (
                    <tr key={transition}>
                      <td style={tdStyle}>{transition}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{(rate * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Source Breakdown */}
          {sourceEntries.length > 0 && (
            <div style={cardStyle}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <BarChart3 size={18} style={{ color: 'var(--color-magenta)' }} />
                Source Breakdown
              </h3>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Source</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {sourceEntries.map(([source, count]) => (
                    <tr key={source}>
                      <td style={tdStyle}>{source || '(unknown)'}</td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
